import { useRuntimeConfig } from '#imports'
import { type Ref, computed } from 'vue'

import type {
  GalleryImage,
  GalleryImageExtraItem,
  ReviewWithCount,
} from '../GalleryNew.types'
import {
  YOUTUBE_VIDEO_THUMBNAIL_URL,
  YOUTUBE_VIDEO_TITLE,
} from '../components/YoutubeVideo/YoutubeVideo.constants'

/**
 * Make sure the ideal index is different
 * in each extra item type.
 *
 * `idealIndex` is the 0-indexed position of
 * the extra item in the carousel.
 */
export const EXTRA_ITEMS_CONFIGURATION = Object.freeze({
  review: {
    idealIndex: 4,
  },
  video: {
    idealIndex: 3,
  },
})

/**
 * Helper function to encapsulate the logic for extra items in the gallery.
 * Each item type will have a different logic: different source and positionning logic.
 */
export function useExtraItems({
  baseCarouselImages,
  review,
}: {
  baseCarouselImages: Ref<GalleryImage[]>
  review: ReviewWithCount | undefined
}) {
  const config = useRuntimeConfig()

  // Pre-compute feature flag once (available at SSR)
  const isVideosInCarouselEnabled =
    config.public.FF_KILL_VIDEOS_IN_NEW_CAROUSEL === false

  // Pre-compute review validity once
  const hasValidReview =
    review &&
    review.id &&
    review.pictures &&
    Array.isArray(review.pictures) &&
    review.pictures.length > 0

  const areThereAnyVideos = isVideosInCarouselEnabled
  const areThereAnyReviews = !!hasValidReview

  const isExtra = (item: GalleryImage) => {
    return 'isExtra' in item && item.isExtra
  }

  const isVideo = (item: GalleryImage) => {
    return isExtra(item) && item.type === 'video'
  }

  const isReview = (item: GalleryImage) => {
    return isExtra(item) && item.type === 'review'
  }

  function injectExtraItemsAtPosition(
    items: Ref<GalleryImage[]>,
  ): GalleryImage[] {
    if (!items.value || !Array.isArray(items.value)) {
      return []
    }

    const result = [...items.value]
    const extraItemsToAdd: GalleryImageExtraItem[] = []

    // Build extra items array directly (no computed chain)
    if (isVideosInCarouselEnabled) {
      extraItemsToAdd.push({
        id: `extra:video-${YOUTUBE_VIDEO_THUMBNAIL_URL}`,
        src: YOUTUBE_VIDEO_THUMBNAIL_URL,
        alt: YOUTUBE_VIDEO_TITLE,
        isExtra: true,
        type: 'video',
        desktopOrder: 0,
      } as const satisfies GalleryImageExtraItem)
    }

    if (hasValidReview) {
      extraItemsToAdd.push({
        src: review.pictures[0],
        alt: review.product?.title || '',
        id: `extra:review-${review.id}`,
        isExtra: true as const,
        type: 'review' as const,
        desktopOrder: 0,
      } satisfies GalleryImageExtraItem)
    }

    if (extraItemsToAdd.length === 0) {
      return items.value
    }

    extraItemsToAdd.forEach((item) => {
      const itemType = item.type
      const itemsConfig = itemType
        ? EXTRA_ITEMS_CONFIGURATION[itemType]
        : undefined

      // If configuration exists, use the ideal index
      // Otherwise, append to the end as a fallback
      const idealIndex = itemsConfig
        ? Math.min(
            itemsConfig.idealIndex || Number.MAX_SAFE_INTEGER,
            result.length,
          )
        : result.length

      result.splice(idealIndex, 0, item)
    })

    return result
  }

  /**
   * This function is used to get the desktop flex-order of an item based
   * its type and the presence of videos and reviews.
   *
   * To prevent hydration mismatches, we calculate the order based on the
   * enabled features rather than the actual presence of items in the list.
   */
  const getDesktopOrder = (
    item: GalleryImage,
    index: number,
    hasVideos: boolean,
    hasReviews: boolean,
  ): number => {
    const isRegular = !isExtra(item)

    // Define order rules as a map for better readability
    // These rules match the original CSS md:order-* classes exactly
    const orderRules = [
      // md:order-1: video (or review if no videos)
      { condition: isVideo(item), order: 1 },
      { condition: isReview(item), order: 1 },

      // md:order-2:
      { condition: index === 1 && hasVideos, order: 2 },
      { condition: index === 3 && !hasVideos && hasReviews, order: 2 },
      { condition: index === 2 && hasVideos && hasReviews, order: 2 },

      // md:order-3:
      { condition: index === 2 && hasVideos && !hasReviews, order: 3 },

      // md:order-4:
      {
        condition: index > 3 && hasVideos && isRegular,
        order: 4 + (index - 4),
      },
    ]

    // Find the first matching rule
    const matchingRule = orderRules.find(({ condition }) => condition)

    // fallback to current position
    return matchingRule?.order ?? index
  }

  const imagesListWithExtraItems = computed(() => {
    return injectExtraItemsAtPosition(baseCarouselImages).map(
      (item, index) => ({
        ...item,
        desktopOrder: getDesktopOrder(
          item,
          index,
          areThereAnyVideos,
          areThereAnyReviews,
        ),
      }),
    )
  })

  const extraItemsIndexes = computed(() => {
    return imagesListWithExtraItems.value.length >
      baseCarouselImages.value.length
      ? imagesListWithExtraItems.value.reduce((acc, image, index) => {
          if (isExtra(image)) {
            acc.push(index)
          }

          return acc
        }, [] as number[])
      : []
  })

  return {
    areThereAnyVideos,
    areThereAnyReviews,
    extraItemsIndexes,
    imagesListWithExtraItems,
    injectExtraItemsAtPosition,
  }
}
